import {GlobalVariables} from "../GlobalVariables";

const DOT_COLOR = "#A6B0FE";
const SPACING = 24;
const DOT_SIZE = 1;
const PERIOD = 3200;
const BAND = 145;
const RING_COUNT = 1;
const RAMP_FRACTION = 0.14;

export default class FooterBackground {
	constructor() {
		this.canvas = document.querySelector(".footer-bg-canvas");
		if (this.canvas) {
			this.container = this.canvas.closest(".footer-cta");
			this.ctx = this.canvas.getContext("2d");
			this.dpr = Math.max(1, window.devicePixelRatio || 1);
			this.dots = [];
			this.running = false;
			this.rafId = null;

			this.draw = this.draw.bind(this);

			// Lu dynamiquement plutôt que codé en dur : reste toujours identique au fond réel
			// (--section-background-color de .section-blue), quelle que soit sa valeur.
			this.bgColor = getComputedStyle(this.container).getPropertyValue("--section-background-color").trim();

			this.resize();
			this.observeVisibility();
		}

		GlobalVariables.resizeCallbacks.push(this);
	}

	resize() {
		if (!this.canvas) {
			return;
		}

		this.width = this.container.clientWidth;
		this.height = this.container.clientHeight;
		this.canvas.width = Math.round(this.width * this.dpr);
		this.canvas.height = Math.round(this.height * this.dpr);
		this.canvas.style.width = this.width + "px";
		this.canvas.style.height = this.height + "px";
		this.ctx.setTransform(this.dpr, 0, 0, this.dpr, 0, 0);

		this.cx = this.width / 2;
		this.cy = this.height / 2;
		this.maxRadius = Math.hypot(Math.max(this.cx, this.width - this.cx), Math.max(this.cy, this.height - this.cy));

		const cols = Math.ceil(this.width / SPACING) + 1;
		const rows = Math.ceil(this.height / SPACING) + 1;
		const offX = (this.width - (cols - 1) * SPACING) / 2;
		const offY = (this.height - (rows - 1) * SPACING) / 2;

		this.dots = [];
		for (let j = 0; j < rows; j++) {
			for (let i = 0; i < cols; i++) {
				const x = offX + i * SPACING;
				const y = offY + j * SPACING;
				this.dots.push({x, y, dist: Math.hypot(x - this.cx, y - this.cy)});
			}
		}

		if (GlobalVariables.reducedMotion) {
			this.drawStatic();
		}
	}

	drawStatic() {
		const ctx = this.ctx;
		const half = DOT_SIZE / 2;

		ctx.fillStyle = this.bgColor;
		ctx.fillRect(0, 0, this.width, this.height);
		ctx.fillStyle = DOT_COLOR;
		this.dots.forEach(dot => ctx.fillRect(dot.x - half, dot.y - half, DOT_SIZE, DOT_SIZE));
	}

	draw(now) {
		const ctx = this.ctx;
		const rampMs = PERIOD * RAMP_FRACTION;
		const travelPeriod = PERIOD - rampMs;
		const half = DOT_SIZE / 2;

		ctx.fillStyle = this.bgColor;
		ctx.fillRect(0, 0, this.width, this.height);
		ctx.fillStyle = DOT_COLOR;

		for (const dot of this.dots) {
			let intensity = 0;

			for (let r = 0; r < RING_COUNT; r++) {
				const phase = ((now - (r * PERIOD) / RING_COUNT) % PERIOD + PERIOD) % PERIOD;
				let radius, v, delta;

				if (phase < rampMs) {
					// maintient l'anneau au centre pendant qu'il grandit d'un point à sa pleine intensité,
					// pour couvrir entièrement les points centraux avant que l'anneau ne se mette à voyager
					radius = 0;
					delta = dot.dist - radius;
					let amp = phase / rampMs;
					amp = amp * amp * (3 - 2 * amp);
					v = amp * Math.exp(-(delta * delta) / (2 * BAND * BAND));
				} else {
					radius = ((phase - rampMs) / travelPeriod) * this.maxRadius;
					delta = dot.dist - radius;
					v = Math.exp(-(delta * delta) / (2 * BAND * BAND));
				}

				if (v > intensity) {
					intensity = v;
				}
			}

			ctx.globalAlpha = 1 - intensity;
			ctx.fillRect(dot.x - half, dot.y - half, DOT_SIZE, DOT_SIZE);
		}

		ctx.globalAlpha = 1;

		if (this.running) {
			this.rafId = requestAnimationFrame(this.draw);
		}
	}

	start() {
		if (this.running || GlobalVariables.reducedMotion) {
			return;
		}

		this.running = true;
		this.rafId = requestAnimationFrame(this.draw);
	}

	stop() {
		this.running = false;
		if (this.rafId) {
			cancelAnimationFrame(this.rafId);
			this.rafId = null;
		}
	}

	observeVisibility() {
		const observer = new IntersectionObserver(entries => {
			entries.forEach(entry => {
				if (entry.isIntersecting) {
					this.start();
				} else {
					this.stop();
				}
			});
		});

		observer.observe(this.container);
	}
}
